import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuotation } from '../../contexts/QuotationContext';
import { useCart } from '../../contexts/CartContext';
import { useToast } from '../../contexts/ToastContext';
import { formatPrice } from '../../utils/priceFormatter';
import BottomNav from '../../components/layouts/BottomNav';
import { FaArrowLeft, FaTrash, FaMinus, FaPlus } from 'react-icons/fa';

import { API_BASE } from '../../utils/url';

const QuotationPage = () => {
  const {
    items,
    quotationTotal,
    removeFromQuotation,
    updateQuantity,
    updateUnitPrice,
    createQuotation,
    clearQuotation,
    addToQuotation
  } = useQuotation();

  const { items: cartItems, clearCart } = useCart();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();

  const [customerDetails, setCustomerDetails] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    gstin_no: '',
    architect_name: ''
  });
  const [meta, setMeta] = useState({
    project: '',
    prepared_by: '',
    confirmed_by: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [priceInputs, setPriceInputs] = useState({});

  // Load cart items into quotation when component mounts
  useEffect(() => {
    if (cartItems.length > 0 && items.length === 0) {
      cartItems.forEach(cartItem => {
        addToQuotation(cartItem, cartItem.quantity);
      });
      clearCart(); // Clear cart after moving to quotation
    }
  }, [cartItems, items.length, addToQuotation, clearCart]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };

  useEffect(() => {
    const nextInputs = {};
    items.forEach(item => {
      nextInputs[item.id] = item.unit_price !== undefined && item.unit_price !== null
        ? item.unit_price.toString()
        : '';
    });
    setPriceInputs(nextInputs);
  }, [items]);

  const handleMetaChange = (e) => {
    const { name, value } = e.target;
    setMeta(prev => ({ ...prev, [name]: value }));
  };

  const handlePriceChange = (productId, value) => {
    setPriceInputs(prev => ({
      ...prev,
      [productId]: value
    }));

    const numeric = parseFloat(value);
    if (!Number.isNaN(numeric) && numeric > 0) {
      updateUnitPrice(productId, numeric);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!customerDetails.name || !customerDetails.email || !customerDetails.phone) {
      showError('Please fill in all customer details');
      return;
    }

    if (items.length === 0) {
      showError('Please add items to quotation');
      return;
    }

    setIsSubmitting(true);

    try {
      const quotation = await createQuotation(customerDetails, meta);
      showSuccess('Quotation created successfully!');

      // Clear quotation and redirect to confirmation
      clearQuotation();
      navigate(`/quotation-confirmation/${quotation.quotation_number}`);
    } catch (error) {
      console.error('Error creating quotation:', error);
      showError(error.response?.data?.message || 'Failed to create quotation');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFirstImageUrl = (item) => {
    // Debug: Log the item data to see what we're working with
    console.log('QuotationPage getFirstImageUrl - item:', item);

    // For quotation items, the image is already processed and stored as a single URL
    if (item.image) {
      let imageUrl = item.image;
      if (!imageUrl.startsWith("http")) {
        imageUrl = API_BASE + imageUrl.replace(/^\/+/, "");
      }
      console.log('QuotationPage getFirstImageUrl - final imageUrl:', imageUrl);
      return imageUrl;
    }
    console.log('QuotationPage getFirstImageUrl - no image found');
    return null;
  };

  if (items.length === 0) {
    return (
      <div style={{ background: "#fff", minHeight: "100vh", paddingBottom: 74 }}>
        {/* Header */}
        <div style={{
          padding: "18px 10px 0 10px",
          background: "#fff",
          position: "sticky",
          top: 0,
          zIndex: 9
        }}>
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "0 6px 10px 6px", marginBottom: 8
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
              <button
                onClick={() => navigate('/cart')}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  marginRight: 10,
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer"
                }}
                aria-label="Back"
              >
                <FaArrowLeft size={20} color="#222" />
              </button>
              <div style={{
                fontWeight: 700,
                fontSize: 21,
                color: "#232323",
                letterSpacing: "0.2px",
                marginTop: 2
              }}>
                Create Quotation
              </div>
            </div>
          </div>
        </div>

        {/* Empty State */}
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "60px 20px",
          textAlign: "center"
        }}>
          <div style={{
            fontSize: 48,
            marginBottom: 16,
            opacity: 0.3
          }}>
            📋
          </div>
          <div style={{
            fontSize: 18,
            fontWeight: 600,
            color: "#333",
            marginBottom: 8
          }}>
            No items in quotation
          </div>
          <div style={{
            fontSize: 14,
            color: "#666",
            marginBottom: 24
          }}>
            Add products to create a quotation
          </div>
          <button
            onClick={() => navigate('/products')}
            style={{
              background: "#333",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "12px 24px",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer"
            }}
          >
            Browse Products
          </button>
        </div>

        <BottomNav />
      </div>
    );
  }

  return (
    <div style={{ background: "#fff", minHeight: "100vh", paddingBottom: 74 }}>
      {/* Header */}
      <div style={{
        padding: "18px 10px 0 10px",
        background: "#fff",
        position: "sticky",
        top: 0,
        zIndex: 9
      }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "0 6px 10px 6px", marginBottom: 8
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
            <button
              onClick={() => navigate('/cart')}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                marginRight: 10,
                display: "flex",
                alignItems: "center",
                cursor: "pointer"
              }}
              aria-label="Back"
            >
              <FaArrowLeft size={20} color="#222" />
            </button>
            <div style={{
              fontWeight: 700,
              fontSize: 21,
              color: "#232323",
              letterSpacing: "0.2px",
              marginTop: 2
            }}>
              Create Quotation
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: "0 16px" }}>
        {/* Items List */}
        <div style={{ marginBottom: 24 }}>
          <h3 style={{
            fontSize: 18,
            fontWeight: 600,
            color: "#333",
            marginBottom: 16
          }}>
            Quotation Items ({items.length})
          </h3>

          {items.map((item) => {
            const imageUrl = getFirstImageUrl(item);
            return (
              <div key={item.id} style={{
                background: "#f8f9fa",
                borderRadius: 12,
                padding: 16,
                marginBottom: 12,
                border: "1px solid #e9ecef"
              }}>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12
                }}>
                  {/* Product Image */}
                  <div style={{
                    width: 60,
                    height: 60,
                    borderRadius: 8,
                    overflow: "hidden",
                    background: "#eee",
                    flexShrink: 0
                  }}>
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={item.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover"
                        }}
                      />
                    ) : (
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        color: "#bbb",
                        fontSize: 12
                      }}>
                        No Image
                      </div>
                    )}
                  </div>

                  {/* Product Details */}
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontWeight: 600,
                      fontSize: 14,
                      color: "#333",
                      marginBottom: 4
                    }}>
                      {item.name}
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 500, color: "#666", marginBottom: 6 }}>
                        Unit Price (editable)
                      </label>
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        border: "1px solid #ddd",
                        borderRadius: 8,
                        padding: "6px 10px",
                        background: "#fff"
                      }}>
                        <span style={{ fontSize: 14, color: "#555" }}>₹</span>
                        <input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={priceInputs[item.id] ?? ''}
                          onChange={(e) => handlePriceChange(item.id, e.target.value)}
                          style={{
                            width: "100%",
                            border: "none",
                            outline: "none",
                            fontSize: 14,
                            color: "#111",
                            background: "transparent"
                          }}
                        />
                      </div>
                      {item.base_price && item.base_price !== item.unit_price && (
                        <div style={{ fontSize: 11, color: "#999", marginTop: 4 }}>
                          Original price: {formatPrice(item.base_price)}
                        </div>
                      )}
                    </div>

                    {/* Quantity Controls */}
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8
                    }}>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        style={{
                          width: 32,
                          height: 32,
                          border: "1px solid #ddd",
                          background: "#fff",
                          borderRadius: 6,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer"
                        }}
                      >
                        <FaMinus size={12} color="#666" />
                      </button>

                      <span style={{
                        minWidth: 40,
                        textAlign: "center",
                        fontSize: 14,
                        fontWeight: 500
                      }}>
                        {item.quantity}
                      </span>

                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        style={{
                          width: 32,
                          height: 32,
                          border: "1px solid #ddd",
                          background: "#fff",
                          borderRadius: 6,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer"
                        }}
                      >
                        <FaPlus size={12} color="#666" />
                      </button>
                    </div>
                  </div>

                  {/* Price and Remove */}
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: 8
                  }}>
                    <div style={{
                      fontWeight: 600,
                      fontSize: 14,
                      color: "#333"
                    }}>
                      {formatPrice(item.total_price)}
                    </div>
                    <button
                      onClick={() => removeFromQuotation(item.id)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#dc3545",
                        cursor: "pointer",
                        padding: 4
                      }}
                    >
                      <FaTrash size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Customer & Quotation Details Form */}
        <div style={{ marginBottom: 24 }}>
          <h3 style={{
            fontSize: 18,
            fontWeight: 600,
            color: "#333",
            marginBottom: 16
          }}>
            Customer Details
          </h3>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 16 }}>
              <label style={{
                display: "block",
                fontSize: 14,
                fontWeight: 500,
                color: "#333",
                marginBottom: 8
              }}>
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={customerDetails.name}
                onChange={handleInputChange}
                required
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "1px solid #ddd",
                  borderRadius: 8,
                  fontSize: 14,
                  background: "#fff"
                }}
                placeholder="Enter your full name"
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{
                display: "block",
                fontSize: 14,
                fontWeight: 500,
                color: "#333",
                marginBottom: 8
              }}>
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={customerDetails.email}
                onChange={handleInputChange}
                required
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "1px solid #ddd",
                  borderRadius: 8,
                  fontSize: 14,
                  background: "#fff"
                }}
                placeholder="Enter your email address"
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{
                display: "block",
                fontSize: 14,
                fontWeight: 500,
                color: "#333",
                marginBottom: 8
              }}>
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={customerDetails.phone}
                onChange={handleInputChange}
                required
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "1px solid #ddd",
                  borderRadius: 8,
                  fontSize: 14,
                  background: "#fff"
                }}
                placeholder="Enter your phone number"
              />
            </div>

            {/* Extended Customer Fields */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 14, fontWeight: 500, color: "#333", marginBottom: 8 }}>Address</label>
              <textarea
                name="address"
                value={customerDetails.address}
                onChange={handleInputChange}
                rows={3}
                style={{ width: "100%", padding: "12px 16px", border: "1px solid #ddd", borderRadius: 8, fontSize: 14, background: "#fff" }}
                placeholder="Enter address"
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div>
                <label style={{ display: "block", fontSize: 14, fontWeight: 500, color: "#333", marginBottom: 8 }}>GSTIN No</label>
                <input
                  type="text"
                  name="gstin_no"
                  value={customerDetails.gstin_no}
                  onChange={handleInputChange}
                  style={{ width: "100%", padding: "12px 16px", border: "1px solid #ddd", borderRadius: 8, fontSize: 14, background: "#fff" }}
                  placeholder="Enter GSTIN"
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 14, fontWeight: 500, color: "#333", marginBottom: 8 }}>Architect Name</label>
                <input
                  type="text"
                  name="architect_name"
                  value={customerDetails.architect_name}
                  onChange={handleInputChange}
                  style={{ width: "100%", padding: "12px 16px", border: "1px solid #ddd", borderRadius: 8, fontSize: 14, background: "#fff" }}
                  placeholder="Enter architect name"
                />
              </div>
            </div>

            <h3 style={{ fontSize: 18, fontWeight: 600, color: '#333', margin: '16px 0' }}>Quotation Meta</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
              <div>
                <label style={{ display: "block", fontSize: 14, fontWeight: 500, color: "#333", marginBottom: 8 }}>Project</label>
                <input
                  type="text"
                  name="project"
                  value={meta.project}
                  onChange={handleMetaChange}
                  style={{ width: "100%", padding: "12px 16px", border: "1px solid #ddd", borderRadius: 8, fontSize: 14, background: "#fff" }}
                  placeholder="Project name"
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 14, fontWeight: 500, color: "#333", marginBottom: 8 }}>Prepared By</label>
                <input
                  type="text"
                  name="prepared_by"
                  value={meta.prepared_by}
                  onChange={handleMetaChange}
                  style={{ width: "100%", padding: "12px 16px", border: "1px solid #ddd", borderRadius: 8, fontSize: 14, background: "#fff" }}
                  placeholder="Sales/Prepared by"
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 14, fontWeight: 500, color: "#333", marginBottom: 8 }}>Confirmed By</label>
                <input
                  type="text"
                  name="confirmed_by"
                  value={meta.confirmed_by}
                  onChange={handleMetaChange}
                  style={{ width: "100%", padding: "12px 16px", border: "1px solid #ddd", borderRadius: 8, fontSize: 14, background: "#fff" }}
                  placeholder="Confirmed by"
                />
              </div>
            </div>

            {/* Total */}
            <div style={{
              background: "#f8f9fa",
              borderRadius: 12,
              padding: 16,
              marginBottom: 24,
              border: "1px solid #e9ecef"
            }}>
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}>
                <span style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: "#333"
                }}>
                  Total Amount:
                </span>
                <span style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#333"
                }}>
                  {formatPrice(quotationTotal)}
                </span>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                width: "100%",
                padding: "16px",
                background: isSubmitting ? "#ccc" : "#000000",
                color: "#ffffff",
                border: "none",
                borderRadius: 12,
                fontSize: 16,
                fontWeight: 600,
                cursor: isSubmitting ? "not-allowed" : "pointer",
                transition: "all 0.2s ease"
              }}
            >
              {isSubmitting ? 'Creating Quotation...' : 'Create Quotation'}
            </button>
          </form>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default QuotationPage;
